/* FOM · layout do mapa — funcao PURA.

   Entra: a arvore (nos com pai, tipo de vinculo e ordem) + o que esta expandido.
   Sai: posicao de cada no visivel e a caixa total.

   Nao toca no DOM, nao guarda estado, nao chama a API. Por isso da para testar
   o layout sem navegador — e por isso ele nao pode "quase" funcionar.

   A ideia estrutural vem do FOM congelado e foi mantida porque descreve bem o
   dominio: filhos ESTRUTURAIS (`kids`) abrem em leque horizontal, e filhos de
   FLUXO (`flow`) empilham em coluna vertical — um fluxo e uma sequencia, e ler
   sequencia na horizontal, junto com a hierarquia, embaralha as duas leituras.
   O que NAO foi trazido: o layout do artifact media a arvore inteira a cada
   render, inclusive o que estava fechado. Aqui so o visivel entra na conta. */
import type { FomNode, StatusClass } from './types.ts';

export type Caixa = { x: number; y: number; w: number; h: number };

export type NoPosicionado = {
  no: FomNode;
  x: number;         /* centro */
  y: number;         /* topo */
  w: number;
  h: number;
  profundidade: number;
  temFilhos: boolean;
  expandido: boolean;
  /* CONFLITO DE LAYOUT (microgate ORGANIZACAO/Financeiro): quando o lugar
     calculado + ajuste manual faz dois IRMAOS se sobreporem, o motor nao
     aceita a colisao — afasta o de cima/direita o minimo necessario, em
     ordem deterministica, e registra aqui o quanto (`desvio`) e que houve
     conflito. Assim: posicao exibida = estrutural + ajuste + desvio. */
  desvio: { x: number; y: number };
  conflito: boolean;
};

export type Layout = {
  posicoes: NoPosicionado[];
  porId: Map<string, NoPosicionado>;
  extensao: Caixa;
  /* ids dos nos que participaram de alguma colisao resolvida */
  conflitos: string[];
};

export type OpcoesLayout = {
  expandidos: Set<string>;
  filtroSt?: Set<StatusClass>;
  mostrarBase?: boolean;
  mostrarUser?: boolean;
  /* Posicoes escolhidas por gente, vindas do banco. Sao DESLOCAMENTOS sobre o
     lugar calculado, e nao coordenadas absolutas: assim, quando a arvore muda
     de forma, o no arrastado acompanha o proprio ramo em vez de ficar orfao
     num canto. */
  ajustes?: Record<string, { x: number; y: number }>;
  /* Resolver colisoes entre irmaos apos aplicar os ajustes (padrao: sim).
     `false` devolve a identidade pura estrutural + ajuste — util para provar
     que o ajuste e SO deslocamento. */
  resolverColisoes?: boolean;
};

/* ORDEM DOS IRMAOS — regra unica do dominio, exportada para que toda leitura
   (mapa, painel, cena de conhecimento) ordene igual e NUNCA dependa da ordem
   em que os nos chegaram da API: fluxo antes de estrutura, depois ordinal,
   depois external_id como desempate total. */
export function compararIrmaos(
  a: { child_kind: string | null; ordinal: number; external_id: string },
  b: { child_kind: string | null; ordinal: number; external_id: string },
): number {
  const ka = a.child_kind === 'flow' ? 0 : 1;
  const kb = b.child_kind === 'flow' ? 0 : 1;
  if (ka !== kb) return ka - kb;
  if (a.ordinal !== b.ordinal) return a.ordinal - b.ordinal;
  return a.external_id.localeCompare(b.external_id);
}

/* GEOMETRIA DO ARTEFATO (gate 0072): cartao 176×58 (raiz 200), capsula
   200×64, 26px entre irmaos, 70px entre niveis — a mesma densidade da cena
   organizacional aprovada. O nome vive em UMA linha (tooltip e painel
   carregam o nome inteiro). */
const LARGURA: Record<number, number> = { 0: 200, 1: 176, 2: 176, 3: 176 };
const ALTURA_NO = 58;
const ALTURA_CAPSULA = 64;
const LARGURA_CAPSULA = 200;
const ESPACO_H = 26;
const ESPACO_V = 70;
const ESPACO_COL = 14;
/* BRANCH-AWARE (gate 0072): um leque estrutural largo nao vira uma fileira
   quilometrica. Acima deste numero de filhos, a fileira quebra em LINHAS
   equilibradas (ceil(n/linhas) por linha), centradas sob o pai — os
   descendentes ficam perto de quem os abriu e a leitura local sobrevive.
   Cada linha guarda ESPACO_LINHA da seguinte. Ordem dos irmaos, parent,
   ordinal e profundidade nao mudam: e so a geometria da fileira. */
export const MAX_POR_LINHA = 5;
const ESPACO_LINHA = 40;
export const ESPACOS = { H: ESPACO_H, V: ESPACO_V, COL: ESPACO_COL, LINHA: ESPACO_LINHA } as const;

/* divide os irmaos estruturais em linhas equilibradas, na ordem do dominio */
export function linhasDe<T>(itens: T[]): T[][] {
  if (itens.length <= MAX_POR_LINHA) return itens.length ? [itens] : [];
  const nLinhas = Math.ceil(itens.length / MAX_POR_LINHA);
  const porLinha = Math.ceil(itens.length / nLinhas);
  const linhas: T[][] = [];
  for (let i = 0; i < itens.length; i += porLinha) linhas.push(itens.slice(i, i + porLinha));
  return linhas;
}

/* um ramo FECHADO com descendentes e uma CAPSULA (representacao agregada):
   caixa propria, um pouco mais larga e mais alta que o cartao */
function largura(profundidade: number, ehFluxo: boolean, capsula = false): number {
  if (capsula) return LARGURA_CAPSULA;
  if (ehFluxo) return 176;
  return LARGURA[Math.min(profundidade, 3)] ?? 176;
}

function altura(_no: FomNode, capsula = false): number {
  return capsula ? ALTURA_CAPSULA : ALTURA_NO;
}

export function calcularLayout(nos: FomNode[], opcoes: OpcoesLayout): Layout {
  const mostrarBase = opcoes.mostrarBase ?? true;
  const mostrarUser = opcoes.mostrarUser ?? true;

  const visiveisPorFiltro = new Set(
    nos.filter((n) => {
      if (n.origin === 'base' && !mostrarBase) return false;
      if (n.origin === 'user' && !mostrarUser) return false;
      if (opcoes.filtroSt && !opcoes.filtroSt.has(n.st)) return false;
      return true;
    }).map((n) => n.external_id),
  );

  const filhos = new Map<string, FomNode[]>();
  let raiz: FomNode | undefined;
  for (const n of nos) {
    if (!n.parent_external_id) { raiz = raiz ?? n; continue; }
    const lista = filhos.get(n.parent_external_id) ?? [];
    lista.push(n);
    filhos.set(n.parent_external_id, lista);
  }
  for (const lista of filhos.values()) lista.sort(compararIrmaos);

  if (!raiz) {
    return { posicoes: [], porId: new Map(), extensao: { x: 0, y: 0, w: 0, h: 0 }, conflitos: [] };
  }

  /* Um no aparece se ele passa no filtro OU se algum descendente passa —
     esconder um pai cujo filho esta visivel quebraria a arvore. */
  const relevante = new Map<string, boolean>();
  function marcar(n: FomNode): boolean {
    const meus = filhos.get(n.external_id) ?? [];
    let algum = visiveisPorFiltro.has(n.external_id);
    for (const f of meus) if (marcar(f)) algum = true;
    relevante.set(n.external_id, algum);
    return algum;
  }
  marcar(raiz);

  function visiveis(n: FomNode): FomNode[] {
    if (!opcoes.expandidos.has(n.external_id)) return [];
    return (filhos.get(n.external_id) ?? []).filter((f) => relevante.get(f.external_id) === true);
  }

  /* 1a passada: mede a caixa de cada subarvore, de baixo para cima. */
  const caixas = new Map<string, { w: number; h: number }>();
  const ehCapsula = (n: FomNode): boolean =>
    !opcoes.expandidos.has(n.external_id) && (filhos.get(n.external_id) ?? []).length > 0;
  function medir(n: FomNode, profundidade: number): { w: number; h: number } {
    const ehFluxo = n.child_kind === 'flow';
    const capsula = ehCapsula(n);
    const lw = largura(profundidade, ehFluxo, capsula);
    const lh = altura(n, capsula);

    const meus = visiveis(n);
    if (meus.length === 0) {
      const c = { w: lw, h: lh };
      caixas.set(n.external_id, c);
      return c;
    }

    const estruturais = meus.filter((f) => f.child_kind !== 'flow');
    const fluxo = meus.filter((f) => f.child_kind === 'flow');

    /* fileira em LINHAS equilibradas: largura = linha mais larga; altura =
       soma das linhas (cada uma com a altura da subarvore mais alta) */
    let larguraEstrutural = 0;
    let alturaEstrutural = 0;
    for (const f of estruturais) medir(f, profundidade + 1);
    const linhas = linhasDe(estruturais);
    linhas.forEach((linha, li) => {
      let lw2 = 0, lh2 = 0;
      for (const f of linha) {
        const c = caixas.get(f.external_id)!;
        lw2 += c.w + ESPACO_H;
        lh2 = Math.max(lh2, c.h);
      }
      lw2 -= ESPACO_H;
      larguraEstrutural = Math.max(larguraEstrutural, lw2);
      alturaEstrutural += lh2 + (li > 0 ? ESPACO_LINHA : 0);
    });

    let larguraFluxo = 0;
    let alturaFluxo = 0;
    for (const f of fluxo) {
      const c = medir(f, profundidade + 1);
      larguraFluxo = Math.max(larguraFluxo, c.w);
      alturaFluxo += c.h + ESPACO_COL;
    }
    if (fluxo.length > 0) alturaFluxo -= ESPACO_COL;

    const juntos = larguraEstrutural + larguraFluxo
      + (larguraEstrutural > 0 && larguraFluxo > 0 ? ESPACO_H * 2 : 0);

    const c = {
      w: Math.max(lw, juntos),
      h: lh + ESPACO_V + Math.max(alturaEstrutural, alturaFluxo),
    };
    caixas.set(n.external_id, c);
    return c;
  }
  medir(raiz, 0);

  /* 2a passada: posiciona, de cima para baixo. */
  const posicoes: NoPosicionado[] = [];
  function colocar(n: FomNode, x0: number, y: number, profundidade: number): void {
    const caixa = caixas.get(n.external_id)!;
    const ehFluxo = n.child_kind === 'flow';
    const capsula = ehCapsula(n);
    const lw = largura(profundidade, ehFluxo, capsula);
    const lh = altura(n, capsula);
    const meus = visiveis(n);
    const temFilhos = (filhos.get(n.external_id) ?? []).length > 0;

    posicoes.push({
      no: n,
      x: x0 + caixa.w / 2,
      y,
      w: lw,
      h: lh,
      profundidade,
      temFilhos,
      expandido: opcoes.expandidos.has(n.external_id),
      desvio: { x: 0, y: 0 },
      conflito: false,
    });

    if (meus.length === 0) return;

    const estruturais = meus.filter((f) => f.child_kind !== 'flow');
    const fluxo = meus.filter((f) => f.child_kind === 'flow');

    const linhas = linhasDe(estruturais);
    const larguraLinha = (linha: FomNode[]): number =>
      linha.reduce((s, f) => s + caixas.get(f.external_id)!.w + ESPACO_H, 0) - (linha.length ? ESPACO_H : 0);
    let larguraEstrutural = 0;
    for (const linha of linhas) larguraEstrutural = Math.max(larguraEstrutural, larguraLinha(linha));

    let larguraFluxo = 0;
    for (const f of fluxo) larguraFluxo = Math.max(larguraFluxo, caixas.get(f.external_id)!.w);

    const total = larguraEstrutural + larguraFluxo
      + (larguraEstrutural > 0 && larguraFluxo > 0 ? ESPACO_H * 2 : 0);

    const inicio = x0 + (caixa.w - total) / 2;
    const cy = y + lh + ESPACO_V;

    /* cada linha centrada na faixa estrutural; a proxima comeca abaixo da
       subarvore mais alta da anterior */
    let ly = cy;
    for (const linha of linhas) {
      const lw2 = larguraLinha(linha);
      let cx = inicio + (larguraEstrutural - lw2) / 2;
      let lh2 = 0;
      for (const f of linha) {
        const c = caixas.get(f.external_id)!;
        colocar(f, cx, ly, profundidade + 1);
        cx += c.w + ESPACO_H;
        lh2 = Math.max(lh2, c.h);
      }
      ly += lh2 + ESPACO_LINHA;
    }

    if (fluxo.length > 0) {
      /* A coluna de fluxo comeca EXATAMENTE onde `medir` a reservou: inicio da
         fileira + largura estrutural + 2*ESPACO_H. Antes partia de `cx`, que ja
         tinha avancado ESPACO_H depois do ultimo filho estrutural — a coluna
         transbordava 26px do box da subarvore e ficava COLADA (gap 0) na
         subarvore vizinha (gate arvore integral). */
      const colX = inicio + larguraEstrutural + (larguraEstrutural > 0 ? ESPACO_H * 2 : 0);
      let fy = cy;
      for (const f of fluxo) {
        const c = caixas.get(f.external_id)!;
        colocar(f, colX + (larguraFluxo - c.w) / 2, fy, profundidade + 1);
        fy += c.h + ESPACO_COL;
      }
    }
  }
  colocar(raiz, 0, 0, 0);

  /* aplicado depois do calculo, antes de medir a extensao: o mapa tem de
     enquadrar o que a pessoa ve, nao o que o algoritmo queria */
  if (opcoes.ajustes) {
    for (const p of posicoes) {
      const ajuste = opcoes.ajustes[p.no.external_id];
      if (!ajuste) continue;
      p.x += ajuste.x;
      p.y += ajuste.y;
    }
  }

  /* um ajuste manual legitimo pode produzir colisao entre irmaos: isso e
     CONFLITO de layout, nao um resultado aceitavel — resolvido aqui, antes
     de medir a extensao, para que o mapa enquadre o que a pessoa ve */
  const conflitos = new Set<string>();
  if (opcoes.resolverColisoes ?? true) {
    resolverColisoes(posicoes, conflitos);
    resolverColisoesCruzadas(posicoes, opcoes.ajustes ?? {}, conflitos);
  }

  const porId = new Map(posicoes.map((p) => [p.no.external_id, p]));

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of posicoes) {
    minX = Math.min(minX, p.x - p.w / 2);
    maxX = Math.max(maxX, p.x + p.w / 2);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y + p.h);
  }
  const extensao: Caixa = posicoes.length
    ? { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
    : { x: 0, y: 0, w: 0, h: 0 };

  return { posicoes, porId, extensao, conflitos: [...conflitos].sort() };
}

/* Colisao entre IRMAOS (mesmo pai, mesmo tipo de vinculo). Irmaos
   estruturais dividem uma fileira: quem esta a direita e empurrado ate
   right(A) + ESPACO_H <= left(B), so quando as caixas tambem se cruzam na
   vertical. Irmaos de fluxo dividem uma coluna: simetrico em y com
   ESPACO_COL. A ordem de varredura e a posicao exibida (esquerda->direita,
   cima->baixo) com desempate pela ordem estrutural — deterministica, e
   respeita o arranjo que a pessoa escolheu ao arrastar. Colisoes entre nos
   de pais diferentes nao sao tocadas (seriam outra gramatica). Tolerancia
   de 0.5px: aritmetica de ponto flutuante do proprio leque nunca vira
   conflito. Nada aqui muda parent, profundidade, ordinal ou subarvore. */
/* Segunda passada — colisoes entre cartoes de PAIS DIFERENTES (gate arvore
   integral). A geometria estrutural (medir/colocar) nunca produz isso: cada
   subarvore tem area propria e as vizinhas ficam a ESPACO_H. So um AJUSTE
   MANUAL pode levar um cartao para dentro de outra subarvore ou de outro
   nivel. Regra: o cartao DESLOCADO (ajuste != 0, ou ja afastado na passada
   de irmaos) e quem se move; quem esta no lugar estrutural nunca sai dele.
   Move-se o MINIMO: entre direita/baixo/esquerda/cima escolhe-se o menor
   empurrao que liberta o cartao de tudo com que se cruza no momento (gap
   ESPACO_H na horizontal, ESPACO_COL na vertical); empate resolvido nessa
   ordem fixa; repete ate nao cruzar nada (limite de iteracoes). Ordem de
   processamento = ordem estrutural (posicoes[]), por isso e deterministica
   e independente de historico de cliques. Nada muda parent, profundidade,
   ordinal ou a posicao de quem nao foi deslocado. */
function resolverColisoesCruzadas(
  posicoes: NoPosicionado[],
  ajustes: Record<string, { x: number; y: number }>,
  conflitos: Set<string>,
): void {
  const deslocado = (p: NoPosicionado): boolean => {
    const a = ajustes[p.no.external_id];
    return (a !== undefined && (a.x !== 0 || a.y !== 0)) || p.desvio.x !== 0 || p.desvio.y !== 0;
  };
  /* "cruza" = fica mais perto do que a gramatica permite: na mesma faixa
     vertical, a menos de ESPACO_H; na mesma faixa horizontal, a menos de
     ESPACO_COL. Assim o cartao deslocado nao so deixa de sobrepor — ganha o
     mesmo respiro que a geometria estrutural garante. */
  const cruza = (a: NoPosicionado, b: NoPosicionado): boolean => {
    const sobreV = a.y < b.y + b.h - 0.5 && b.y < a.y + a.h - 0.5;
    const sobreH = a.x - a.w / 2 < b.x + b.w / 2 - 0.5 && b.x - b.w / 2 < a.x + a.w / 2 - 0.5;
    const pertoH = a.x - a.w / 2 < b.x + b.w / 2 + ESPACO_H - 0.5 && b.x - b.w / 2 < a.x + a.w / 2 + ESPACO_H - 0.5;
    const pertoV = a.y < b.y + b.h + ESPACO_COL - 0.5 && b.y < a.y + a.h + ESPACO_COL - 0.5;
    return (sobreV && pertoH) || (sobreH && pertoV);
  };
  const fixos = posicoes.filter((p) => !deslocado(p));
  const finalizados: NoPosicionado[] = [...fixos];
  /* Quanto e preciso empurrar `p` numa direcao ate nao cruzar NADA do que
     ja esta finalizado — simulado de forma monotona (so avanca naquela
     direcao), por isso termina e nunca oscila entre dois vizinhos. */
  const empurrarAte = (p: NoPosicionado, ux: number, uy: number): { d: number; contra: NoPosicionado[] } => {
    const c = { x: p.x, y: p.y, w: p.w, h: p.h } as NoPosicionado;
    const tocados = new Set<NoPosicionado>();
    let total = 0;
    for (let iter = 0; iter < 128; iter++) {
      const contra = finalizados.filter((q) => q !== p && cruza(c, q));
      if (contra.length === 0) break;
      for (const q of contra) tocados.add(q);
      let passo = 0;
      for (const q of contra) {
        if (ux > 0) passo = Math.max(passo, q.x + q.w / 2 + ESPACO_H - (c.x - c.w / 2));
        else if (ux < 0) passo = Math.max(passo, (c.x + c.w / 2) - (q.x - q.w / 2 - ESPACO_H));
        else if (uy > 0) passo = Math.max(passo, q.y + q.h + ESPACO_COL - c.y);
        else passo = Math.max(passo, (c.y + c.h) - (q.y - ESPACO_COL));
      }
      c.x += ux * passo; c.y += uy * passo; total += passo;
    }
    return { d: total, contra: [...tocados] };
  };
  for (const p of posicoes) {
    if (!deslocado(p)) continue;
    if (!finalizados.some((q) => q !== p && cruza(p, q))) { finalizados.push(p); continue; }
    /* direita, baixo, esquerda, cima — escolhe o MENOR deslocamento total;
       empate resolvido nessa ordem fixa (determinismo) */
    const candidatos: { d: number; ux: number; uy: number; contra: NoPosicionado[] }[] = [
      { ...empurrarAte(p, 1, 0), ux: 1, uy: 0 }, { ...empurrarAte(p, 0, 1), ux: 0, uy: 1 },
      { ...empurrarAte(p, -1, 0), ux: -1, uy: 0 }, { ...empurrarAte(p, 0, -1), ux: 0, uy: -1 },
    ];
    const melhor = candidatos.reduce((m, c) => (c.d < m.d ? c : m));
    p.x += melhor.ux * melhor.d; p.y += melhor.uy * melhor.d;
    p.desvio.x += melhor.ux * melhor.d; p.desvio.y += melhor.uy * melhor.d;
    p.conflito = true; conflitos.add(p.no.external_id);
    for (const q of melhor.contra) { q.conflito = true; conflitos.add(q.no.external_id); }
    finalizados.push(p);
  }
}

function resolverColisoes(posicoes: NoPosicionado[], conflitos: Set<string>): void {
  const indice = new Map(posicoes.map((p, i) => [p.no.external_id, i]));
  const grupos = new Map<string, NoPosicionado[]>();
  for (const p of posicoes) {
    if (!p.no.parent_external_id) continue;
    const chave = p.no.parent_external_id + (p.no.child_kind === 'flow' ? '|flow' : '|kids');
    const g = grupos.get(chave) ?? [];
    g.push(p);
    grupos.set(chave, g);
  }
  const esq = (p: NoPosicionado): number => p.x - p.w / 2;
  const dir = (p: NoPosicionado): number => p.x + p.w / 2;
  for (const [chave, g] of grupos) {
    if (g.length < 2) continue;
    const horizontal = !chave.endsWith('|flow');
    const ordem = g.slice().sort((a, b) =>
      (horizontal ? esq(a) - esq(b) : a.y - b.y) || (indice.get(a.no.external_id)! - indice.get(b.no.external_id)!));
    for (let i = 1; i < ordem.length; i++) {
      const cur = ordem[i]!;
      for (let j = 0; j < i; j++) {
        const prev = ordem[j]!;
        if (horizontal) {
          const cruzaV = cur.y < prev.y + prev.h && prev.y < cur.y + cur.h;
          if (!cruzaV) continue;
          const minimo = dir(prev) + ESPACO_H;
          if (esq(cur) < minimo - 0.5) {
            const d = minimo - esq(cur);
            cur.x += d; cur.desvio.x += d;
            cur.conflito = prev.conflito = true;
            conflitos.add(cur.no.external_id); conflitos.add(prev.no.external_id);
          }
        } else {
          const cruzaH = esq(cur) < dir(prev) && esq(prev) < dir(cur);
          if (!cruzaH) continue;
          const minimo = prev.y + prev.h + ESPACO_COL;
          if (cur.y < minimo - 0.5) {
            const d = minimo - cur.y;
            cur.y += d; cur.desvio.y += d;
            cur.conflito = prev.conflito = true;
            conflitos.add(cur.no.external_id); conflitos.add(prev.no.external_id);
          }
        }
      }
    }
  }
}
